// ════════════════════════════════════════════════════════════
// /paiements — Sprint 4 (mobile-first)
// ════════════════════════════════════════════════════════════
// Hub des encaissements. CTA "Nouvel encaissement" en haut (tweak Sidi #2),
// tabs workflow (à déposer / en compense / à comptabiliser / …), modules par
// mode (cash/cheque/wallet/virement), search, badges anomalies.
//
// Mobile-first : cards stack, FAB en bas droite.
// ════════════════════════════════════════════════════════════
import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  AlertCircle,
  AlertTriangle,
  Search,
  Wallet,
  Banknote,
  FileText,
  ScrollText,
  ArrowRightLeft,
  Plus,
  type LucideIcon,
} from 'lucide-react';
import {
  usePaiements,
  usePaiementCounts,
  useAnomalyCountsByPaiement,
  usePaiementsADeposer,
  usePaiementsAComptabiliser,
  usePaiementsEnCompense,
  useBanques,
  isPaiementMemeBanque,
} from '@/queries/paiements';
import { useComptesTresorerie } from '@/queries/tresorerie';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import {
  STATUS_LABELS,
  STATUS_COLORS,
  canCreatePaiement,
  type Paiement,
  type PaiementMode,
  type PaiementStatus,
} from '@ecr/core/paiements';
import { formatMRU } from '@ecr/core/utils';

export const Route = createFileRoute('/_authed/paiements/')({
  component: PaiementsIndex,
});

// ─── Tabs status ─────────────────────────────────────────
// Workflow V1 fidèle (refonte Sidi 2026-05-19) — par mode d'encaissement :
//
//  CHÈQUE :
//    Saisie → À déposer → Upload bordereau OK :
//      • même banque → À comptabiliser (argent_recu=true auto)
//      • banque ≠   → En compense (argent_recu=false, attend compensation)
//        → Comptable confirme reception → À comptabiliser
//      → Comptabilisé (ref Sage)
//  CASH :
//    Saisie (argent_recu=true) → À déposer (à verser à la banque)
//    → Upload bordereau de versement OK → À comptabiliser
//    → Comptabilisé (ref Sage)
//  WALLET :
//    Saisie + photo capture → À comptabiliser (argent_recu=true direct)
//    → Comptabilisé (ref Sage)
//  VIREMENT :
//    Saisie → En compense (argent_recu=false direct)
//    → Comptable confirme reception → À comptabiliser
//    → Comptabilisé (ref Sage)
//
// Le comptable VOIT tous les onglets mais ne peut AGIR (mark argent_recu,
// confirmer Sage) que sur "En compense" et "À comptabiliser".
const TABS = [
  { key: 'a_deposer', label: 'À déposer', statuses: undefined as PaiementStatus[] | undefined },
  { key: 'en_compense', label: 'En compense', statuses: undefined as PaiementStatus[] | undefined },
  {
    key: 'a_comptabiliser',
    label: 'À comptabiliser',
    statuses: undefined as PaiementStatus[] | undefined,
  },
  { key: 'confirme', label: 'Comptabilisés', statuses: ['confirme'] as PaiementStatus[] },
  { key: 'rejete', label: 'Rejetés', statuses: ['rejete'] as PaiementStatus[] },
  { key: 'toutes', label: 'Toutes', statuses: undefined as PaiementStatus[] | undefined },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const MODE_LABELS: Record<PaiementMode, string> = {
  cash: 'Cash',
  cheque: 'Chèque',
  wallet: 'Wallet',
  traite: 'Traite',
  virement: 'Virement',
};

const MODE_ICONS: Record<PaiementMode, LucideIcon> = {
  cash: Banknote,
  cheque: FileText,
  wallet: Wallet,
  traite: ScrollText,
  virement: ArrowRightLeft,
};

function PaiementsIndex() {
  const { profile } = useAuth();
  // Le comptable atterrit directement sur sa file de travail : les
  // encaissements à comptabiliser dans Sage (demande Sidi 2026-08-20).
  const [activeTab, setActiveTab] = useState<TabKey>(() =>
    profile?.role === 'comptable' ? 'a_comptabiliser' : 'a_deposer',
  );
  const [search, setSearch] = useState('');
  const [modeFilter, setModeFilter] = useState<PaiementMode | null>(null);
  // Saisir un encaissement = DAF only (Sidi/Abidine) — les autres consultent/agissent.
  const canCreate = canCreatePaiement(profile?.role ?? null);

  // V1 fidèle : tous les rôles voient tous les onglets (comptable VOIT
  // mais n'agit que sur En compense + À comptabiliser, gating dans
  // computeActions).
  const visibleTabs = TABS;

  const currentTab = visibleTabs.find((t) => t.key === activeTab) ?? visibleTabs[0];
  const isADeposerTab = activeTab === 'a_deposer';
  const isEnCompenseTab = activeTab === 'en_compense';
  const isAComptabiliserTab = activeTab === 'a_comptabiliser';

  // Tabs custom : fetch les en_attente + filtre côté front.
  const fetchStatus =
    isAComptabiliserTab || isEnCompenseTab
      ? (['en_attente'] as PaiementStatus[])
      : currentTab.statuses
        ? [...currentTab.statuses]
        : undefined;

  const {
    data: paiementsRegularRaw = [],
    isLoading: loadingRegular,
    error: errorRegular,
  } = usePaiements({
    status: fetchStatus,
    mode: modeFilter ?? undefined,
    search,
  });
  const { data: banques = [] } = useBanques();
  const { data: comptes = [] } = useComptesTresorerie();

  // Filtres tab-specific (post-fetch).
  const paiementsRegular = (() => {
    let list = paiementsRegularRaw;
    if (isAComptabiliserTab) {
      // Argent reçu + pas encore comptabilisé (ref Sage vide).
      // Cash sur compte bancaire : exige bordereau de versement uploadé
      // (sinon reste à déposer). Cash en caisse (Caisse Mauripe) : déjà à sa
      // place, comptabilisable directement (décision Sidi 2026-08-27).
      const caisseIds = new Set(comptes.filter((c) => c.type === 'caisse').map((c) => c.id));
      list = list.filter(
        (p) =>
          p.argentRecu &&
          !p.refCompta &&
          (p.mode !== 'cash' ||
            p.bordereauUrl !== null ||
            (!!p.banqueEncaissementId && caisseIds.has(p.banqueEncaissementId))),
      );
    } else if (isEnCompenseTab) {
      // En compense : attend compensation interbancaire.
      // Cas 1 : chèque réseau bancaire ≠ avec bordereau, argent_recu=false
      // Cas 2 : virement, argent_recu=false
      list = list.filter((p) => {
        if (p.argentRecu) return false;
        if (p.mode === 'virement') return true;
        if (p.mode === 'cheque' && p.bordereauUrl && !isPaiementMemeBanque(p, banques, comptes)) {
          return true;
        }
        return false;
      });
    }
    return list;
  })();

  const {
    data: paiementsADeposer = [],
    isLoading: loadingADeposer,
    error: errorADeposer,
  } = usePaiementsADeposer();
  const { data: paiementsAComptabiliserList = [] } = usePaiementsAComptabiliser();
  const { data: paiementsEnCompenseList = [] } = usePaiementsEnCompense();

  // Workflow "à déposer" (Sprint 4 patch) : chèques sans bordereau,
  // mode et activité peuvent encore filtrer + search local.
  // Le comptable n'a rien à faire ici donc on lui masque l'onglet (cf. TABS).
  const paiements = isADeposerTab
    ? paiementsADeposer.filter((p) => {
        if (modeFilter && p.mode !== modeFilter) return false;
        if (search.trim()) {
          const s = search.trim().toLowerCase();
          return (
            p.code.toLowerCase().includes(s) ||
            p.clientNom.toLowerCase().includes(s) ||
            (p.numCheque ?? '').toLowerCase().includes(s)
          );
        }
        return true;
      })
    : paiementsRegular;

  const isLoading = isADeposerTab ? loadingADeposer : loadingRegular;
  const error = isADeposerTab ? errorADeposer : errorRegular;

  const { data: counts } = usePaiementCounts();
  const { data: anomalyCounts = {} } = useAnomalyCountsByPaiement();
  const aDeposerCount = paiementsADeposer.length;
  const aComptabiliserCount = paiementsAComptabiliserList.length;
  const enCompenseCount = paiementsEnCompenseList.length;

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Header compact mobile — Sidi tweak #2 : CTA Nouveau en haut */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-foreground text-xl font-semibold tracking-tight sm:text-2xl">
            Encaissements
          </h1>
        </div>
        <p className="text-muted-foreground hidden text-sm sm:block">
          Cash, chèques, wallets &amp; virements clients
        </p>

        {/* CTA principal Sidi tweak #2 — en haut, DAF only */}
        {canCreate && (
          <Link to="/paiements/new" className="block">
            <Button className="w-full gap-2 sm:w-auto" size="lg">
              <Plus className="size-4" />
              Nouvel encaissement
            </Button>
          </Link>
        )}
      </div>

      {/* Workflow modules (par mode) — denser sur mobile. Le mode « cash » n'est
          plus saisissable (décision Sidi 2026-09-11) : sa tuile n'apparaît que
          s'il reste des encaissements cash historiques. */}
      <div
        className={cn(
          'grid gap-1.5 sm:gap-2.5',
          paiements.some((p) => p.mode === 'cash') ? 'grid-cols-4' : 'grid-cols-3',
        )}
      >
        {(
          [
            ...(paiements.some((p) => p.mode === 'cash') ? (['cash'] as PaiementMode[]) : []),
            'wallet',
            'cheque',
            'virement',
          ] as PaiementMode[]
        ).map((m) => (
          <WorkflowModule
            key={m}
            icon={MODE_ICONS[m]}
            label={MODE_LABELS[m]}
            count={paiements.filter((p) => p.mode === m).length}
            active={modeFilter === m}
            onClick={() => setModeFilter(modeFilter === m ? null : m)}
          />
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
        <Input
          type="search"
          placeholder="Code, client, n° chèque…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Tabs status sticky — accessible pendant le scroll de la liste */}
      <div className="bg-background border-border sticky top-12 z-20 -mx-4 overflow-x-auto border-b sm:top-14 sm:mx-0">
        <div className="flex min-w-max gap-1 px-4 sm:min-w-0 sm:px-0">
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors',
                activeTab === tab.key
                  ? 'border-primary text-foreground'
                  : 'text-muted-foreground hover:text-foreground border-transparent',
              )}
            >
              {tab.label}
              {counts && tab.statuses?.length === 1 && (
                <span className="text-muted-foreground ml-1.5 text-xs">
                  ({counts[tab.statuses[0]!] ?? 0})
                </span>
              )}
              {tab.key === 'a_deposer' && aDeposerCount > 0 && (
                <span className="ml-1.5 inline-flex size-5 items-center justify-center rounded-full bg-orange-500 px-1.5 text-xs font-bold text-white">
                  {aDeposerCount}
                </span>
              )}
              {tab.key === 'en_compense' && enCompenseCount > 0 && (
                <span className="ml-1.5 inline-flex size-5 items-center justify-center rounded-full bg-yellow-500 px-1.5 text-xs font-bold text-white">
                  {enCompenseCount}
                </span>
              )}
              {tab.key === 'a_comptabiliser' && aComptabiliserCount > 0 && (
                <span className="ml-1.5 inline-flex size-5 items-center justify-center rounded-full bg-blue-500 px-1.5 text-xs font-bold text-white">
                  {aComptabiliserCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <PaiementCardSkeleton key={i} />
          ))}
        </div>
      ) : error ? (
        <Card>
          <CardContent className="flex items-center gap-3 py-6">
            <AlertCircle className="size-5 text-red-600" />
            <p className="text-sm">
              Erreur de chargement : {error instanceof Error ? error.message : 'inconnu'}
            </p>
          </CardContent>
        </Card>
      ) : paiements.length === 0 ? (
        <EmptyState tab={currentTab.label} hasSearch={search.length > 0} canCreate={canCreate} />
      ) : (
        <PaiementList paiements={paiements} anomalyCounts={anomalyCounts} />
      )}

      {/* FAB mobile (DAF only) */}
      {canCreate && (
        <Link
          to="/paiements/new"
          className="bg-primary text-primary-foreground fixed bottom-[calc(4rem+env(safe-area-inset-bottom,0px)+0.75rem)] right-6 z-40 flex size-14 items-center justify-center rounded-full shadow-lg transition-transform hover:scale-105 active:scale-95 sm:hidden"
          aria-label="Nouvel encaissement"
        >
          <Plus className="size-6" />
        </Link>
      )}
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────
function EmptyState({
  tab,
  hasSearch,
  canCreate,
}: {
  tab: string;
  hasSearch: boolean;
  canCreate: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        <Wallet className="text-muted-foreground size-12" />
        <div>
          <p className="text-foreground font-medium">
            {hasSearch ? 'Aucun résultat' : 'Aucun encaissement'}
          </p>
          <p className="text-muted-foreground text-sm">
            {hasSearch
              ? 'Aucun encaissement ne correspond à ta recherche.'
              : `Aucun encaissement dans « ${tab.toLowerCase()} ».`}
          </p>
        </div>
        {!hasSearch && canCreate && (
          <Link to="/paiements/new">
            <Button variant="outline" className="mt-2 gap-2">
              <Plus className="size-4" />
              Créer un encaissement
            </Button>
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Workflow module compteur ─────────────────────────────
interface WorkflowModuleProps {
  icon: LucideIcon;
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}

function WorkflowModule({ icon: Icon, label, count, active, onClick }: WorkflowModuleProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'bg-card flex min-h-[88px] flex-col items-center justify-between gap-1 rounded-lg border p-2 transition-all',
        'sm:min-h-0 sm:flex-row sm:justify-start sm:gap-3 sm:p-3 sm:text-left',
        'hover:bg-accent active:scale-[0.98]',
        active ? 'border-primary ring-primary/30 ring-2' : 'border-border',
      )}
    >
      <div
        className={cn(
          'flex size-7 shrink-0 items-center justify-center rounded-md sm:size-10',
          'bg-primary/10 text-primary',
        )}
      >
        <Icon className="size-4 sm:size-5" />
      </div>
      <div className="flex w-full min-w-0 flex-col items-center sm:items-start">
        <p className="text-foreground text-xl font-bold tabular-nums leading-none sm:text-2xl">
          {count}
        </p>
        <p className="text-muted-foreground mt-1 w-full truncate text-center text-[10px] font-medium sm:mt-0.5 sm:text-left sm:text-xs">
          {label}
        </p>
      </div>
    </button>
  );
}

// ─── Liste cards ──────────────────────────────────────────
function PaiementList({
  paiements,
  anomalyCounts,
}: {
  paiements: Paiement[];
  anomalyCounts: Record<string, number>;
}) {
  return (
    <div className="grid gap-2.5">
      {paiements.map((p) => (
        <PaiementCard key={p.id} paiement={p} anomalyCount={anomalyCounts[p.id] ?? 0} />
      ))}
    </div>
  );
}

function PaiementCard({ paiement, anomalyCount }: { paiement: Paiement; anomalyCount: number }) {
  const statusColors = STATUS_COLORS[paiement.status];
  const dateStr = useMemo(
    () =>
      new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short' }).format(
        paiement.createdAt,
      ),
    [paiement.createdAt],
  );
  const ModeIcon = MODE_ICONS[paiement.mode];

  return (
    <Link
      to="/paiements/$id"
      params={{ id: paiement.id }}
      className="block transition-all hover:opacity-90 active:scale-[0.99]"
    >
      <Card>
        <CardContent className="flex flex-col gap-2 py-3">
          {/* Ligne 1 : code + badges */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-foreground font-mono text-sm font-semibold">{paiement.code}</span>
            <span
              className="rounded-full px-2 py-0.5 text-xs font-medium"
              style={{ color: statusColors.fg, backgroundColor: statusColors.bg }}
            >
              {STATUS_LABELS[paiement.status]}
            </span>
            <span className="text-muted-foreground inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium dark:bg-slate-800">
              <ModeIcon className="size-3" />
              {MODE_LABELS[paiement.mode]}
            </span>
            {paiement.argentRecu ? (
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-950/50 dark:text-green-300">
                ✓ En caisse
              </span>
            ) : (
              <span className="text-muted-foreground rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium dark:bg-slate-800">
                ⏳ Attente argent
              </span>
            )}
            {paiement.isRattrapage && (
              <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800 dark:bg-orange-950/50 dark:text-orange-200">
                Rattrapage {paiement.rattrapageJours}j
              </span>
            )}
            {anomalyCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-orange-500 px-2 py-0.5 text-xs font-medium text-white">
                <AlertTriangle className="size-3" />
                {anomalyCount}
              </span>
            )}
          </div>

          {/* Ligne 2 : client + montant */}
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-foreground truncate text-sm font-medium">{paiement.clientNom}</p>
            <p className="text-foreground shrink-0 font-mono text-base font-semibold tabular-nums">
              {formatMRU(paiement.montant)}
            </p>
          </div>

          {/* Ligne 3 : meta (date + num cheque/ref) */}
          <div className="text-muted-foreground flex items-center gap-2 text-xs">
            <span>{dateStr}</span>
            {paiement.numCheque && (
              <>
                <span>·</span>
                <span>Chèque {paiement.numCheque}</span>
              </>
            )}
            {paiement.refTransaction && (
              <>
                <span>·</span>
                <span className="truncate">Ref {paiement.refTransaction}</span>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function PaiementCardSkeleton() {
  return (
    <Card>
      <CardContent className="space-y-2 py-3">
        <div className="flex gap-2">
          <div className="bg-muted h-5 w-20 animate-pulse rounded" />
          <div className="bg-muted h-5 w-16 animate-pulse rounded-full" />
        </div>
        <div className="flex justify-between">
          <div className="bg-muted h-5 w-1/2 animate-pulse rounded" />
          <div className="bg-muted h-5 w-24 animate-pulse rounded" />
        </div>
        <div className="bg-muted h-3 w-2/3 animate-pulse rounded" />
      </CardContent>
    </Card>
  );
}
